import { observer } from 'mobx-react';
import React, { useCallback, useMemo, useReducer } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Chip } from '@mui/material';

import { emptyArray, SearchType } from 'app/constants';
import useIsMobile from 'hooks/use-is-mobile';
import useStores from 'hooks/use-stores';
import HeightAnimationEffect from 'main/components/HeightAnimationEffect';
import { usePublicSubscriptionTemplates } from 'queries/subscription-templates/use-public-subscription-templates';

import useStyles from './styles';

interface Props {
    className?: string;
}

const COUNT = 8;

const Templates: React.FC<Props> = ({ className }) => {
    const history = useHistory();
    const isMobile = useIsMobile();
    const { classes, cx } = useStyles();

    const { searchStore } = useStores();

    const [isExpanded, toggleIsExpanded] = useReducer((state) => !state, false);

    const { data = emptyArray } = usePublicSubscriptionTemplates();

    const templates = useMemo(
        () =>
            data.map((item) => ({
                ...item,
                alias: item.alias
                    // TODO: Remove emoji and other special characters in admin panel settings
                    .replace(/[^\p{L}\p{N}\p{P}\p{Z}]/gu, '')
                    .replace(/\s+/g, ' ')
                    .trim()
            })),
        [data]
    );

    const isCollapsible = isMobile && templates.length > COUNT;

    const scope = useMemo(
        () => (isCollapsible && isExpanded === false ? templates.slice(0, COUNT) : templates),
        [isCollapsible, isExpanded, templates]
    );

    const handleClick = useCallback(
        ({ query, aliasUrl }) =>
            (event: React.MouseEvent) => {
                event.preventDefault();
                history.push(aliasUrl || `/search?query=${query}`);
                searchStore.setParameters({
                    type: SearchType.simple,
                    query: query,
                    software: '',
                    version: '',
                    state: {}
                });
                return searchStore.performFetch();
            },
        [history, searchStore]
    );

    return (
        <HeightAnimationEffect className={className}>
            <div className={classes.container}>
                {scope.map(({ alias, aliasUrl, query }, index) => (
                    <Chip
                        key={index}
                        component={Link}
                        {...{ disableRipple: true }}
                        target="_blank"
                        to={aliasUrl || `/search?query=${query}`}
                        variant="outlined"
                        className={classes.chip}
                        label={alias}
                        onClick={handleClick({ query, aliasUrl })}
                    />
                ))}
                {isMobile && templates.length > COUNT ? (
                    <Chip
                        {...{ disableRipple: true }}
                        variant="outlined"
                        className={classes.chip}
                        label={
                            <>
                                {isExpanded ? 'Show less' : 'Show more'}
                                &nbsp;
                                <ExpandMoreIcon className={cx(classes.arrow, { [classes._rotated]: isExpanded })} />
                            </>
                        }
                        onClick={toggleIsExpanded}
                    />
                ) : null}
            </div>
        </HeightAnimationEffect>
    );
};

export default observer(Templates);
